import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";

const API = `${getBackendUrl()}/api`;

/**
 * Mistral-first insights: calls /analytics/insights/generate and falls back to
 * rule-based local markdown only when the API fails or Mistral is unavailable.
 */
export function useMistralFirstInsights({
  kind,
  title,
  buildLocalMarkdown,
  context = {},
  enabled = true,
  scopeKey,
}) {
  const { scope } = useScope();
  const [markdown, setMarkdown] = useState("");
  const [provider, setProvider] = useState("local");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const requestIdRef = useRef(0);

  const scopeParams = {
    district_code: scope.districtCode || undefined,
    block_code: scope.blockCode || undefined,
    udise_code: scope.udiseCode || undefined,
  };

  const fetchInsights = useCallback(async () => {
    if (!enabled || typeof buildLocalMarkdown !== "function") {
      setMarkdown("");
      setProvider("local");
      setLoading(false);
      return;
    }

    const localMarkdown = buildLocalMarkdown() || "";
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API}/analytics/insights/generate`,
        {
          kind,
          title,
          local_markdown: localMarkdown,
          context,
        },
        { params: scopeParams }
      );
      if (requestId !== requestIdRef.current) return null;

      const text = response?.data?.ai_insights || localMarkdown;
      const usedProvider = response?.data?.provider || "local";
      setMarkdown(text);
      setProvider(usedProvider);
      setGeneratedAt(response?.data?.generated_at || new Date().toISOString());
      return usedProvider;
    } catch (err) {
      if (requestId !== requestIdRef.current) return null;
      setError(err?.response?.data?.detail || err.message || "AI insights unavailable");
      setMarkdown(localMarkdown);
      setProvider("local");
      setGeneratedAt(new Date().toISOString());
      return "local";
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, buildLocalMarkdown, kind, title, scopeKey, scopeParams.district_code, scopeParams.block_code, scopeParams.udise_code]);

  useEffect(() => {
    fetchInsights();
  }, [scopeKey, fetchInsights]);

  return {
    markdown,
    provider,
    loading,
    error,
    generatedAt,
    refresh: fetchInsights,
    isMistral: provider === "mistral",
  };
}
